<script lang="ts" setup>
import { computed, watch, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useField } from 'vee-validate'
import type { FormField, SelectOption } from '../types/formTypeStructure'
import {
  useResponsiveSize,
  getLocalizedText
} from '../utils/formHelpers'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import { AlertCircle, ChevronDown } from "lucide-vue-next";

interface Props {
  field: FormField & { fieldType: 'dropdown', selectOptions: SelectOption[] }
  name?: string
  defaultValue?: string
  currentLang?: string
  className?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: string];
  'change': [value: string];
  'input': [value: string];
}>();

const size = useResponsiveSize()
const { width, height, borderRadius } = size.calculateSize(window.innerWidth)
const fieldName = props.name ?? props.field.fieldName.toString()

const {
  value,
  errorMessage,
  handleChange,
  setTouched,
  setValue
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

// États réactifs
const isOpen = ref(false)
const searchQuery = ref('')
const filteredOptions = ref<SelectOption[]>(props.field.selectOptions || [])
const selectedOption = ref<SelectOption | null>(null)
const hoveredOptionIndex = ref<number | null>(null)
const isFocused = ref(false)
const dropdownRef = ref<HTMLDivElement>()
const inputRef = ref<HTMLInputElement>()

// Émettre les événements quand la valeur change
watch(value, (newValue) => {
  emit('update:modelValue', newValue);
  emit('change', newValue);
  emit('input', newValue);
});

const placeholder = getLocalizedText(props.field.placeholder, props.currentLang)
const label = getLocalizedText(props.field.label, props.currentLang)
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

// Fonction utilitaire pour créer un slug (équivalent à createSlug en React)
const createSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9\s-]/g, '') // Supprime les caractères spéciaux
    .replace(/\s+/g, '-') // Remplace les espaces par des tirets
    .replace(/-+/g, '-') // Supprime les tirets multiples
    .trim()
    .replace(/^-+|-+$/g, ''); // Supprime les tirets en début et fin
}

// Initialiser la valeur sélectionnée si defaultValue est fourni
onMounted(() => {
  if (props.defaultValue) {
    const option = props.field.selectOptions?.find(opt => opt.value === props.defaultValue)
    if (option) {
      selectedOption.value = option
      searchQuery.value = getLocalizedText(option.label, props.currentLang)
    }
  }
  
  // Mettre à jour la sélection si la valeur change via le formulaire
  if (value.value && value.value !== selectedOption.value?.value) {
    const option = props.field.selectOptions?.find(opt => opt.value === value.value)
    if (option) {
      selectedOption.value = option
      searchQuery.value = getLocalizedText(option.label, props.currentLang)
    }
  }
})

// Filtrer les options selon la recherche
watch(searchQuery, (newQuery) => {
  if (!props.field.enableSearch) {
    filteredOptions.value = props.field.selectOptions || []
    return
  }

  if (!newQuery.trim()) {
    filteredOptions.value = props.field.selectOptions || []
    return
  }

  const querySlug = createSlug(newQuery)
  const filtered = props.field.selectOptions?.filter(option => {
    const labelText = getLocalizedText(option.label, props.currentLang)
    const labelSlug = createSlug(labelText)
    return labelSlug.includes(querySlug)
  }) || []

  filteredOptions.value = filtered
})

// Fermer le dropdown quand on clique à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node
  
  if (dropdownRef.value && dropdownRef.value.contains(target)) {
    return
  }
  
  isOpen.value = false
  hoveredOptionIndex.value = null
  
  // Restaurer le texte de l'option sélectionnée si pas de recherche active
  if (selectedOption.value && props.field.enableSearch) {
    searchQuery.value = getLocalizedText(selectedOption.value.label, props.currentLang)
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

const handleInputChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const value = target.value
  searchQuery.value = value
  
  if (props.field.enableSearch) {
    isOpen.value = true
    hoveredOptionIndex.value = null
    
    // Si on efface le texte, effacer aussi la sélection
    if (!value.trim() && selectedOption.value) {
      selectedOption.value = null
      setValue('')
    }
  }
}

const handleOptionSelect = (option: SelectOption) => {
  selectedOption.value = option
  searchQuery.value = getLocalizedText(option.label, props.currentLang)
  setValue(option.value)
  isOpen.value = false
  hoveredOptionIndex.value = null
  setTouched(true)
}

const handleInputClick = () => {
  if (props.field.enableSearch) {
    isOpen.value = !isOpen.value
    if (selectedOption.value && !isOpen.value) {
      // Vider le texte pour permettre la recherche
      searchQuery.value = ''
    }
  } else {
    isOpen.value = !isOpen.value
  }
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    isOpen.value = false
    hoveredOptionIndex.value = null
    if (selectedOption.value && props.field.enableSearch) {
      searchQuery.value = getLocalizedText(selectedOption.value.label, props.currentLang)
    }
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (filteredOptions.value.length === 1 && filteredOptions.value[0]) {
      handleOptionSelect(filteredOptions.value[0])
    }
  } else if (e.key === 'ArrowDown') {
    e.preventDefault()
    isOpen.value = true
  }
}

const handleFocus = () => {
  isFocused.value = true
}

const handleBlur = () => {
  isFocused.value = false
}

// Styles calculés dynamiquement
const inputStyles = computed(() => ({
  width: `${width}px`,
  height: `${height}px`,
  borderRadius: `${borderRadius}px`,
  border: '1.5px solid #04406E',
  color: '#04406E',
  backgroundColor: '#f0f4f7',
  padding: '0 12px',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  cursor: props.field.enableSearch ? 'text' : 'pointer',
  paddingRight: '40px',
  ...(isFocused.value && {
    boxShadow: '0 0 0 2px #b8c9d6',
    borderColor: '#04406E',
  })
}))

const dropdownStyles = computed(() => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: '#f0f4f7',
  border: '1.5px solid #04406E',
  borderTop: 'none',
  borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 1000,
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
}))

const getOptionStyles = (index: number, isSelected: boolean = false) => ({
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '1rem',
  color: '#04406E',
  borderBottom: '1px solid #e6f0f5',
  transition: 'background-color 0.1s ease',
  backgroundColor: hoveredOptionIndex.value === index ? '#e6f0f5' : 
                   isSelected ? '#b8c9d6' : 'transparent',
})

const arrowStyles = computed(() => ({
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: `translateY(-50%) ${isOpen.value ? 'rotate(180deg)' : 'rotate(0deg)'}`,
  transition: 'transform 0.2s ease',
  pointerEvents: 'none',
  width: '16px',
  height: '16px',
  color: '#008080',
}))

const noResultsStyles = {
  padding: '8px 12px',
  color: '#888',
  fontStyle: 'italic',
  fontSize: '1rem',
}

const baseClasses = 'transition-all focus:ring-2 focus:ring-[#04406E]'
</script>

<template>
  <div class="dropdown-field-container">
    <!-- Label dupliqué au-dessus du select -->
    <div class="dropdown-field-label-container">
      <span class="dropdown-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <!-- Si la recherche n'est pas activée, utiliser l'ancien comportement avec select natif -->
      <select
          v-if="!props.field.enableSearch"
        :name="fieldName"
        :value="value"
        :class="[baseClasses, props.className]"
        :style="inputStyles"
        @change="(e) => { handleChange((e.target as HTMLSelectElement).value); setTouched(true); }"
          @focus="handleFocus"
          @blur="handleBlur"
      >
        <option value="" disabled selected>
          {{ placeholder || `Sélectionner ${label}` }}
        </option>

        <option
          v-for="option in props.field.selectOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ getLocalizedText(option.label, props.currentLang) }}
        </option>
      </select>

      <!-- Dropdown avec recherche activée -->
      <div v-else ref="dropdownRef" class="relative">
        <input
          :name="fieldName"
          ref="inputRef"
          type="text"
          :class="[baseClasses, props.className]"
          :style="inputStyles"
          :value="searchQuery"
          @input="handleInputChange"
          @click="handleInputClick"
          @keydown="handleKeyDown"
          @focus="handleFocus"
          @blur="handleBlur"
          :placeholder="placeholder || `Rechercher ou sélectionner ${label}`"
          autocomplete="off"
        />
        
        <!-- Flèche dropdown -->
        <ChevronDown
          :style="arrowStyles"
          :size="16"
          class="absolute pointer-events-none"
        />

        <!-- Liste déroulante avec recherche -->
        <div
          v-if="isOpen && props.field.enableSearch"
          :style="dropdownStyles"
          class="dropdown-list"
        >
          <div
            v-if="filteredOptions.length === 0"
            :style="noResultsStyles"
          >
            Aucun résultat trouvé
          </div>
          
          <div
            v-else
            v-for="(option, index) in filteredOptions"
            :key="option.value"
            :style="getOptionStyles(index, selectedOption?.value === option.value)"
            @click="handleOptionSelect(option)"
            @mouseenter="hoveredOptionIndex = index"
            @mouseleave="hoveredOptionIndex = null"
            class="dropdown-option"
          >
            {{ getLocalizedText(option.label, props.currentLang) }}
          </div>
        </div>
      </div>

      <p v-if="errorMessage" class="text-sm text-red-500 mt-1">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.dropdown-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.dropdown-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.dropdown-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #f0f4f7;
  border: 1.5px solid #04406E;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.dropdown-option {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 1rem;
  color: #04406E;
  border-bottom: 1px solid #e6f0f5;
  transition: background-color 0.1s ease;
}

.dropdown-option:hover {
  background-color: #e6f0f5;
}

.dropdown-option:last-child {
  border-bottom: none;
}

/* Styles pour le select natif quand enableSearch est false */
select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23008080' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}
</style>
